import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Shield, AlertTriangle, ExternalLink, CheckCircle2, XCircle,
  RefreshCw, Loader2, Search, Filter, Download, ChevronLeft, ChevronRight,
  Send, FileText, ArrowRight, FileMinus2,
} from 'lucide-react';
import FneCertifyModal from '../../components/FneCertifyModal';
import FneRefundModal from '../../components/FneRefundModal';
import FneStickerWidget from '../../components/admin/FneStickerWidget';
import { FneCertStatusBadge, FnePriorityBadge } from '../../components/admin/fne/FneStatusBadges';
import { FneExclusionBadge, FneExclusionChip } from '../../components/admin/fne/FneExclusionBadge';
import FneContractInvoicesTab, { FneModeSwitch } from '../../components/admin/fne/FneContractInvoicesTab';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 10;

/* -------------------------------------------------------------------------- */
/*                              Helper components                             */
/* -------------------------------------------------------------------------- */

const KpiCard = ({ label, value, sublabel, tone = 'emerald', onClick, testId }) => {
  const palette = {
    emerald: { num: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100', icon: 'text-emerald-600' },
    amber:   { num: 'text-amber-600',   bg: 'bg-amber-50',   border: 'border-amber-100',   icon: 'text-amber-600' },
    rose:    { num: 'text-rose-600',    bg: 'bg-rose-50',    border: 'border-rose-100',    icon: 'text-rose-600' },
    slate:   { num: 'text-slate-700',   bg: 'bg-slate-50',   border: 'border-slate-100',   icon: 'text-slate-600' },
  }[tone];
  return (
    <Card className={`${palette.border} hover:shadow-sm transition-shadow`} data-testid={testId}>
      <CardContent className="p-4 flex flex-col gap-2">
        <div className="flex items-start justify-between">
          <div className={`w-8 h-8 rounded-lg ${palette.bg} ${palette.icon} flex items-center justify-center`}>
            <Shield className="w-4 h-4" />
          </div>
        </div>
        <div>
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</div>
          <div className={`text-3xl font-bold ${palette.num} leading-tight`}>{value}</div>
        </div>
        <div className="text-xs text-slate-500 leading-tight">{sublabel}</div>
        {onClick && (
          <button
            onClick={onClick}
            className={`mt-1 self-start inline-flex items-center gap-1 text-xs font-medium ${palette.num} hover:underline`}
            data-testid={`${testId}-link`}
          >
            Voir les lignes <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </CardContent>
    </Card>
  );
};

const TabPill = ({ active, label, onClick, count, tone = 'emerald', testId }) => {
  const activeCls = {
    emerald: 'text-emerald-700 border-emerald-600',
    amber: 'text-amber-700 border-amber-500',
    rose: 'text-rose-700 border-rose-500',
    slate: 'text-slate-700 border-slate-600',
  }[tone];
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
        active ? activeCls : 'text-slate-500 border-transparent hover:text-slate-700'
      }`}
    >
      {label}
      {count !== undefined && (
        <span className={`ml-1.5 text-xs ${active ? 'opacity-100' : 'opacity-60'}`}>({count})</span>
      )}
    </button>
  );
};

/* -------------------------------------------------------------------------- */
/*                                  Page                                      */
/* -------------------------------------------------------------------------- */

export default function FneInvoices() {
  const [mode, setMode] = useState('spot');
  const [filter, setFilter] = useState('not_certified');
  const [data, setData] = useState({ items: [], counts: {}, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [activeModal, setActiveModal] = useState(null);
  const [refundTarget, setRefundTarget] = useState(null);
  const [page, setPage] = useState(1);

  // New: search, sort, selection, bulk-cert state
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('recent'); // recent | urgent | amount_desc
  const [selected, setSelected] = useState(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam = filter === 'all' ? '' : `&status=${filter}`;
      const { data: resp } = await axios.get(
        `${API}/fne/invoices?page=${page}&pageSize=${PAGE_SIZE}${statusParam}`
      );
      setData(resp);
      setSelected(new Set()); // reset selection on reload
    } catch (err) {
      console.error('FNE list load error', err);
      toast.error('Erreur chargement', { description: err?.response?.data?.detail || err.message });
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  // Reload when filter or page changes; reset page on filter change is handled in setFilter wrappers
  useEffect(() => { load(); }, [load]);

  const c = data.counts || {};
  const env = data.environment || 'dev';
  const certStartDate = data.certStartDate || null;

  const updateFilter = (next) => {
    setFilter(next);
    setPage(1);
  };

  /* ---------------------- Derived: filtered + sorted ---------------------- */
  const visibleItems = useMemo(() => {
    let list = [...(data.items || [])];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((it) => {
        return (
          (it.invoiceNumber || '').toLowerCase().includes(q)
          || (it.operationNumber || '').toLowerCase().includes(q)
          || (it.client?.companyName || '').toLowerCase().includes(q)
          || (it.client?.email || '').toLowerCase().includes(q)
          || (it.client?.ncc || '').toLowerCase().includes(q)
        );
      });
    }
    list.sort((a, b) => {
      if (sortBy === 'amount_desc') return (b.amount || 0) - (a.amount || 0);
      if (sortBy === 'urgent') {
        // NCC manquants & blocked d'abord, puis age desc
        const aBlocked = !a.preflight?.ready && a.fne?.status !== 'certified' ? 1 : 0;
        const bBlocked = !b.preflight?.ready && b.fne?.status !== 'certified' ? 1 : 0;
        if (aBlocked !== bBlocked) return bBlocked - aBlocked;
        return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
      }
      // recent
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });
    return list;
  }, [data.items, search, sortBy]);

  /* ------------------------- Selection helpers ---------------------------- */
  const certifiableItems = useMemo(
    () => visibleItems.filter((it) => it.fne?.status !== 'certified' && it.preflight?.ready && !it.excluded),
    [visibleItems]
  );
  const allSelected = certifiableItems.length > 0 && certifiableItems.every((i) => selected.has(i.id));
  const toggleAll = () => {
    if (allSelected) {
      const next = new Set(selected);
      certifiableItems.forEach((i) => next.delete(i.id));
      setSelected(next);
    } else {
      const next = new Set(selected);
      certifiableItems.forEach((i) => next.add(i.id));
      setSelected(next);
    }
  };
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /* --------------------------- Actions ----------------------------------- */
  const certifyBulk = async (ids) => {
    if (ids.length === 0) return;
    if (!window.confirm(`Certifier ${ids.length} opération(s) FNE en séquence ?`)) return;
    setBulkRunning(true);
    let ok = 0;
    let fail = 0;
    for (const id of ids) {
      try {
        await axios.post(`${API}/fne/sign-spot/${id}`, {});
        ok += 1;
        toast.success(`✓ ${id.slice(0, 8)}… certifiée (${ok}/${ids.length})`);
      } catch (err) {
        fail += 1;
        toast.error(`✗ Échec ${id.slice(0, 8)}…`, {
          description: err?.response?.data?.detail || err.message,
        });
      }
    }
    setBulkRunning(false);
    toast.message(`Lot terminé : ${ok} certifiée(s) · ${fail} échec(s)`);
    setSelected(new Set());
    load();
  };

  const exportCsv = () => {
    const rows = visibleItems.map((it) => ({
      facture: it.invoiceNumber,
      operation: it.operationNumber,
      client: it.client?.companyName || '',
      ncc: it.client?.ncc || '',
      montant: it.amount || 0,
      paiement: it.paymentMethod || '',
      etat: it.fne?.status,
      reference_dgi: it.fne?.reference || '',
      preflight_ready: it.preflight?.ready ? 'oui' : 'non',
      erreurs: (it.preflight?.issues || []).map((i) => i.field).join('|'),
    }));
    if (rows.length === 0) {
      toast.error('Rien à exporter');
      return;
    }
    const cols = Object.keys(rows[0]);
    const csv = [
      cols.join(','),
      ...rows.map((r) => cols.map((k) => `"${String(r[k] ?? '').replace(/"/g, '""')}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `certifications_fne_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Export CSV (${rows.length} lignes)`);
  };

  /* ------------------------------- Render -------------------------------- */
  if (mode === 'contract') {
    return <FneContractInvoicesTab mode={mode} setMode={setMode} env={env} />;
  }

  return (
    <div className="space-y-5 p-4 md:p-6" data-testid="fne-invoices-page">
      {/* ============== Header ============== */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, ui-sans-serif, system-ui' }}>
            <Shield className="w-6 h-6 inline-block mr-1.5 -mt-1 text-emerald-600" />
            Certifications FNE
            <Badge variant="outline" className="ml-2 text-[10px] align-middle">{env.toUpperCase()}</Badge>
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Suivez les factures à certifier, les blocages et l&apos;état des stickers DGI.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <FneModeSwitch mode={mode} setMode={setMode} />
          <Button variant="outline" size="sm" onClick={load} className="h-9" data-testid="fne-list-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Rafraîchir
          </Button>
          <Link to="/admin/fne/api-logs">
            <Button variant="outline" size="sm" className="h-9" data-testid="fne-api-logs-link">
              <FileText className="h-3.5 w-3.5 mr-1.5" /> Journal API
            </Button>
          </Link>
          <Link to="/admin/settings/fne">
            <Button variant="outline" size="sm" className="h-9">Paramètres FNE</Button>
          </Link>
          <Button variant="outline" size="sm" onClick={exportCsv} className="h-9" data-testid="fne-export-btn">
            <Download className="h-3.5 w-3.5 mr-1.5" /> Exporter
          </Button>
          <Button
            size="sm"
            onClick={() => certifyBulk(certifiableItems.map((i) => i.id))}
            disabled={bulkRunning || certifiableItems.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white h-9"
            data-testid="fne-certify-ready-btn"
          >
            {bulkRunning ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Shield className="h-3.5 w-3.5 mr-1.5" />}
            Certifier le lot prêt ({certifiableItems.length})
          </Button>
        </div>
      </div>

      {/* ============== Banner périmètre FNE ============== */}
      {certStartDate ? (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50/60 border border-emerald-200 text-xs" data-testid="fne-scope-banner">
          <Shield className="w-3.5 h-3.5 text-emerald-700" />
          <span className="text-slate-700">
            Périmètre FNE actif <strong>({env.toUpperCase()})</strong> :
            certification possible pour les opérations <strong>à partir du{' '}
            {new Date(certStartDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}</strong>.
          </span>
          {(c.excluded ?? 0) > 0 && (
            <button
              onClick={() => updateFilter('excluded')}
              className="ml-auto text-emerald-700 hover:underline font-medium"
              data-testid="fne-scope-banner-link"
            >
              {c.excluded} exclusions →
            </button>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50/60 border border-amber-200 text-xs" data-testid="fne-scope-banner-missing">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-700" />
          <span className="text-slate-700">
            Aucune date de début de certification définie pour <strong>{env.toUpperCase()}</strong>.
            Toutes les opérations sont actuellement éligibles à la certification.
          </span>
          <Link to="/admin/settings/fne" className="ml-auto text-amber-700 hover:underline font-medium">
            Configurer →
          </Link>
        </div>
      )}

      {/* ============== KPIs ============== */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard
          label="Prêtes à certifier"
          value={c.ready ?? 0}
          sublabel="Opérations pouvant être envoyées maintenant"
          tone="emerald"
          onClick={() => updateFilter('ready')}
          testId="fne-kpi-ready"
        />
        <KpiCard
          label="Bloquées"
          value={c.blocked ?? 0}
          sublabel="Action requise avant certification"
          tone="amber"
          onClick={() => updateFilter('blocked')}
          testId="fne-kpi-blocked"
        />
        <KpiCard
          label="NCC client manquant"
          value={c.missing_ncc ?? 0}
          sublabel="Clients à compléter ou basculer en B2C"
          tone="rose"
          onClick={() => updateFilter('blocked')}
          testId="fne-kpi-missing-ncc"
        />
        <KpiCard
          label="Déjà certifiées"
          value={c.certified ?? 0}
          sublabel="Opérations certifiées avec succès"
          tone="slate"
          onClick={() => updateFilter('certified')}
          testId="fne-kpi-certified"
        />
        <FneStickerWidget />
      </div>

      {/* ============== Filters bar ============== */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-3">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher (facture, client, opération…)"
                className="pl-9 h-10 bg-white border-slate-200"
                data-testid="fne-search"
              />
            </div>
            <div className="flex flex-wrap items-center border-b border-slate-100 lg:border-0">
              <TabPill active={filter === 'all'} label="Toutes" onClick={() => updateFilter('all')} testId="fne-tab-all" />
              <TabPill active={filter === 'ready'} label="Prêtes" onClick={() => updateFilter('ready')} tone="emerald" count={c.ready} testId="fne-tab-ready" />
              <TabPill active={filter === 'blocked'} label="Bloquées" onClick={() => updateFilter('blocked')} tone="rose" count={c.blocked} testId="fne-tab-blocked" />
              <TabPill active={filter === 'not_certified'} label="Non certifiées" onClick={() => updateFilter('not_certified')} tone="amber" testId="fne-tab-not-certified" />
              <TabPill active={filter === 'certified'} label="Certifiées" onClick={() => updateFilter('certified')} count={c.certified} testId="fne-tab-certified" />
              <TabPill active={filter === 'failed'} label="Échecs" onClick={() => updateFilter('failed')} tone="rose" testId="fne-tab-failed" />
              <TabPill active={filter === 'excluded'} label="Exclusions FNE" onClick={() => updateFilter('excluded')} tone="slate" count={c.excluded} testId="fne-tab-excluded" />
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <Button variant="outline" size="sm" className="h-9" data-testid="fne-filters-advanced">
                <Filter className="w-3.5 h-3.5 mr-1.5" /> Filtres avancés
              </Button>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[150px] h-9" data-testid="fne-sort">
                  <SelectValue placeholder="Tri" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Plus récent</SelectItem>
                  <SelectItem value="urgent">Plus urgent</SelectItem>
                  <SelectItem value="amount_desc">Montant ↓</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ============== Bulk actions bar ============== */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={allSelected}
              onCheckedChange={toggleAll}
              disabled={certifiableItems.length === 0}
              data-testid="fne-select-all"
            />
            <span className="text-slate-700 font-medium">{selected.size} sélectionnée{selected.size > 1 ? 's' : ''}</span>
          </div>
          <Button
            size="sm"
            onClick={() => certifyBulk(Array.from(selected))}
            disabled={selected.size === 0 || bulkRunning}
            className="bg-emerald-600 hover:bg-emerald-700 text-white h-9"
            data-testid="fne-bulk-certify"
          >
            {bulkRunning ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Shield className="h-3.5 w-3.5 mr-1.5" />}
            Certifier la sélection
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} className="h-9" data-testid="fne-bulk-export">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Exporter
          </Button>
          <Link to="/admin/clients?tab=companies&readiness=incomplete">
            <Button variant="outline" size="sm" className="h-9" data-testid="fne-bulk-relance">
              <Send className="w-3.5 h-3.5 mr-1.5" /> Relancer les clients incomplets
            </Button>
          </Link>
          <div className="ml-auto flex items-center gap-2 text-xs text-slate-500">
            <span>{visibleItems.length} opérations</span>
            <span>·</span>
            <span>{PAGE_SIZE} / page</span>
          </div>
        </CardContent>
      </Card>

      {/* ============== Main Table ============== */}
      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
            </div>
          ) : visibleItems.length === 0 ? (
            <div className="text-center py-12 text-slate-500 text-sm">
              Aucune opération à afficher pour ce filtre.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/60 border-b border-slate-200 hover:bg-slate-50/60">
                    <TableHead className="w-10 pl-4 py-3"></TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Facture / Opération</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Client</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Montant</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">État certification</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Vérification</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right pr-4">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleItems.map((item) => {
                    const cert = item.fne?.status;
                    const ready = item.preflight?.ready;
                    const issues = item.preflight?.issues || [];
                    const nccIssue = issues.some((i) => /ncc|compteContribuable/i.test(`${i.field}${i.message}`));
                    const excluded = !!item.excluded;
                    const exclusionReason = item.exclusionReason;
                    const exclusionDetail = item.exclusionDetail;
                    const isCertifiable = ready && cert !== 'certified' && !excluded;
                    return (
                      <TableRow
                        key={item.id}
                        className={`border-b border-slate-100 hover:bg-emerald-50/20 transition-colors ${
                          selected.has(item.id) ? 'bg-emerald-50/40' : ''
                        } ${excluded ? 'opacity-75' : ''}`}
                        data-testid={`fne-row-${item.id}`}
                      >
                        <TableCell className="pl-4 py-3 align-top">
                          <Checkbox
                            checked={selected.has(item.id)}
                            onCheckedChange={() => toggleOne(item.id)}
                            disabled={!isCertifiable}
                            data-testid={`fne-select-${item.id}`}
                          />
                        </TableCell>
                        <TableCell className="py-3 align-top">
                          <Link
                            to={`/admin/operations/${item.id}`}
                            className="font-mono text-sm font-medium text-blue-700 hover:underline"
                          >
                            {item.invoiceNumber || '—'}
                          </Link>
                          <div className="text-[11px] text-slate-400 font-mono">{item.operationNumber}</div>
                          <div className="mt-1"><FnePriorityBadge item={item} /></div>
                        </TableCell>
                        <TableCell className="py-3 align-top">
                          <div className="text-sm font-semibold text-slate-800">{item.client?.companyName || '—'}</div>
                          {item.client?.hasNcc ? (
                            <div className="text-[11px] text-emerald-700 mt-0.5">
                              <CheckCircle2 className="w-3 h-3 inline-block mr-0.5" />
                              NCC OK <span className="font-mono text-slate-500">{item.client.ncc}</span>
                            </div>
                          ) : (
                            <div className="text-[11px] text-amber-700 mt-0.5 inline-flex items-center gap-0.5">
                              <AlertTriangle className="w-3 h-3" />
                              NCC manquant → certifiera en B2C
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="py-3 align-top">
                          <div className="text-sm font-medium text-slate-800">
                            {item.amount ? `${Number(item.amount).toLocaleString('fr-FR')} XOF` : '—'}
                          </div>
                          <div className="text-[11px] text-slate-400">{item.paymentMethod || '—'}</div>
                        </TableCell>
                        <TableCell className="py-3 align-top">
                          {excluded ? (
                            <FneExclusionBadge reason={exclusionReason} label={item.exclusionLabel} detail={exclusionDetail} />
                          ) : (
                            <FneCertStatusBadge
                              certStatus={cert}
                              preflightReady={ready}
                              hasNccIssue={nccIssue}
                              hasOtherBlock={!ready && issues.length > 0}
                            />
                          )}
                          {item.fne?.reference && (
                            <div className="text-[10px] font-mono text-slate-500 mt-1 truncate max-w-[160px]" title={item.fne.reference}>
                              {item.fne.reference}
                            </div>
                          )}
                          {item.fne?.errorMessage && (
                            <div className="text-[10px] text-rose-600 mt-1 truncate max-w-[180px]" title={item.fne.errorMessage}>
                              {item.fne.errorMessage}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="py-3 align-top">
                          {excluded ? (
                            <FneExclusionChip reason={exclusionReason} detail={exclusionDetail} />
                          ) : ready ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700 text-xs">
                              <CheckCircle2 className="h-3.5 w-3.5" /> Pré-flight : OK
                            </span>
                          ) : nccIssue ? (
                            <span className="inline-flex items-center gap-1 text-rose-700 text-xs font-medium">
                              <XCircle className="h-3.5 w-3.5" /> NCC client manquant
                            </span>
                          ) : (
                            <div className="text-xs space-y-0.5">
                              <div className="inline-flex items-center gap-1 text-amber-700 font-medium">
                                <AlertTriangle className="h-3.5 w-3.5" /> Document manquant
                              </div>
                              {issues.slice(0, 2).map((iss, i) => (
                                <div key={i} className="text-rose-600 ml-4 truncate max-w-[180px]" title={iss.message}>
                                  · {iss.field}
                                </div>
                              ))}
                              {issues.length > 2 && (
                                <div className="text-slate-500 ml-4">+{issues.length - 2} autres</div>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="py-3 pr-4 align-top text-right">
                          {excluded ? (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled
                              className="h-8 px-3 text-xs text-slate-400 border-slate-200 cursor-not-allowed"
                              title={exclusionDetail || 'Cette opération est exclue de la FNE.'}
                              data-testid={`fne-excluded-disabled-${item.id}`}
                            >
                              <Shield className="h-3.5 w-3.5 mr-1" /> Exclue
                            </Button>
                          ) : cert === 'certified' || cert === 'refunded_partial' ? (
                            <div className="flex items-center justify-end gap-2">
                              {item.fne?.token && (
                                <a
                                  href={item.fne.token}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-emerald-700 underline hover:text-emerald-800"
                                  data-testid={`fne-verify-${item.id}`}
                                >
                                  Vérifier <ExternalLink className="h-3 w-3" />
                                </a>
                              )}
                              {cert === 'refunded_partial' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={async () => {
                                    try {
                                      const { data } = await axios.get(`${API}/fne/credit-notes/by-operation/${item.id}`);
                                      const latest = (data.items || [])[0];
                                      if (latest?.credit_note_number) {
                                        window.open(`${API}/documents/download/${latest.credit_note_number}`, '_blank');
                                      } else {
                                        toast.warning('Aucun PDF d\'avoir trouvé');
                                      }
                                    } catch (e) {
                                      toast.error('Erreur de chargement de l\'avoir');
                                    }
                                  }}
                                  className="h-8 px-2 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                  data-testid={`fne-download-credit-${item.id}`}
                                  title="Télécharger l'avoir PDF"
                                >
                                  <FileMinus2 className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setRefundTarget(item)}
                                className="h-8 px-3 text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
                                data-testid={`fne-refund-row-${item.id}`}
                                title="Émettre un avoir FNE"
                              >
                                <FileMinus2 className="h-3.5 w-3.5 mr-1" />
                                Avoir
                              </Button>
                            </div>
                          ) : cert === 'refunded_total' ? (
                            <div className="flex items-center justify-end gap-2">
                              <span className="inline-flex items-center gap-1 text-xs text-slate-500 italic">
                                <FileMinus2 className="h-3 w-3" /> Avoir total émis
                              </span>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={async () => {
                                  try {
                                    const { data } = await axios.get(`${API}/fne/credit-notes/by-operation/${item.id}`);
                                    const latest = (data.items || [])[0];
                                    if (latest?.credit_note_number) {
                                      window.open(`${API}/documents/download/${latest.credit_note_number}`, '_blank');
                                    } else {
                                      toast.warning('Aucun PDF d\'avoir trouvé pour cette opération');
                                    }
                                  } catch (e) {
                                    toast.error('Erreur de chargement de l\'avoir');
                                  }
                                }}
                                className="h-8 px-3 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                data-testid={`fne-download-credit-${item.id}`}
                                title="Télécharger l'avoir PDF"
                              >
                                <FileMinus2 className="h-3.5 w-3.5 mr-1" />
                                Avoir PDF
                              </Button>
                            </div>
                          ) : isCertifiable ? (
                            <Button
                              size="sm"
                              onClick={() => setActiveModal(item)}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3 text-xs"
                              data-testid={`fne-certify-row-${item.id}`}
                            >
                              <Shield className="h-3.5 w-3.5 mr-1" />
                              Certifier
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setActiveModal(item)}
                              className="h-8 px-3 text-xs"
                              data-testid={`fne-view-row-${item.id}`}
                            >
                              Voir
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>

        {/* Pagination */}
        {!loading && (data.totalPages || 1) >= 1 && (data.total || 0) > 0 && (
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/40 text-xs text-slate-600">
            <div data-testid="fne-pagination-summary">
              Affichage de <span className="font-medium text-slate-800">{((data.page || page) - 1) * (data.pageSize || PAGE_SIZE) + 1}</span> à{' '}
              <span className="font-medium text-slate-800">{Math.min((data.page || page) * (data.pageSize || PAGE_SIZE), data.total || 0)}</span> sur{' '}
              <span className="font-medium text-slate-800">{data.total || 0}</span> opérations
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost" size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="h-8 w-8 p-0"
                data-testid="fne-page-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              {Array.from({ length: data.totalPages || 1 }, (_, i) => i + 1).slice(0, 7).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`h-8 min-w-[2rem] px-2 rounded-md text-sm font-medium transition-colors ${
                    p === (data.page || page)
                      ? 'bg-emerald-600 text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  data-testid={`fne-page-${p}`}
                >
                  {p}
                </button>
              ))}
              <Button
                variant="ghost" size="sm"
                disabled={page >= (data.totalPages || 1)}
                onClick={() => setPage((p) => Math.min(data.totalPages || 1, p + 1))}
                className="h-8 w-8 p-0"
                data-testid="fne-page-next"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Modal Certify */}
      {activeModal && (
        <FneCertifyModal
          open={!!activeModal}
          onClose={() => setActiveModal(null)}
          onSuccess={() => { setActiveModal(null); load(); }}
          targetType="spot"
          targetId={activeModal.id}
          targetLabel={activeModal.invoiceNumber}
        />
      )}

      {/* Modal Refund (Avoir) */}
      {refundTarget && (
        <FneRefundModal
          open={!!refundTarget}
          onClose={() => setRefundTarget(null)}
          onSuccess={() => { setRefundTarget(null); load(); }}
          targetType="spot"
          targetId={refundTarget.id}
          targetLabel={refundTarget.invoiceNumber}
        />
      )}

      {/* Alerte NCC manquants (conservée mais discrète) */}
      {c.missing_ncc > 0 && (
        <Card className="border-rose-200 bg-rose-50/40">
          <CardContent className="py-3 px-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm flex-1">
              <div className="font-semibold text-rose-800">
                {c.missing_ncc} opération(s) bloquée(s) par un NCC client manquant
              </div>
              <p className="text-rose-700 text-xs mt-0.5">
                Sans NCC, la certification tombera en B2C (template particulier). Pour vos clients entreprises,
                renseignez leur NCC (format CI : <code className="font-mono bg-rose-100 px-1 rounded">1234567A</code>).
              </p>
            </div>
            <Link to="/admin/clients?tab=companies&readiness=incomplete">
              <Button
                size="sm"
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="fne-rollout-cta-btn"
              >
                Préparer les NCC clients →
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
